import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../atoms/button";
import { getProducts } from "../../products/functions";
import { parseSerialList } from "./parseSerialList";

const SERIAL_PLACEHOLDER = "MG0001\nMG0002\nMG0003";

function productLabel(product) {
  const name = product?.user?.name || product?.name || product?._id || "";
  const id = product?._id ? ` #${product._id.slice(-6)}` : "";
  return `${name}${id}`.trim() || product?._id || "Pedido";
}

export default function TagsSelectionHub({ user, onContinue }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [serialText, setSerialText] = useState("");

  const { serials, duplicateCount } = useMemo(
    () => parseSerialList(serialText),
    [serialText]
  );

  const productCount = selectedProductIds.size;
  const serialCount = serials.length;
  const canContinue = productCount > 0 || serialCount > 0;

  const loadProducts = useCallback(
    async (q) => {
      if (!user?.token) {
        setProducts([]);
        return;
      }
      setLoadingProducts(true);
      const query = q?.trim() || undefined;
      const data = await getProducts(
        user.token,
        undefined,
        undefined,
        query ? undefined : "QUEUE",
        undefined,
        undefined,
        undefined,
        query
      );
      if (data?.type === "success") {
        setProducts(Array.isArray(data.data) ? data.data : []);
      } else if (Array.isArray(data)) {
        setProducts(data);
      } else {
        setProducts([]);
      }
      setLoadingProducts(false);
    },
    [user?.token]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, loadProducts]);

  const toggleProduct = (productId) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleContinue = () => {
    if (!canContinue) {
      return;
    }
    onContinue?.({
      productIds: [...selectedProductIds],
      serialNumbers: serials,
    });
  };

  return (
    <div className="text-orange-100 flex flex-col gap-4 pb-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="border border-[#d59106] rounded p-4 flex flex-col gap-3">
          <h2 className="text-[#d59106] font-semibold">Pedidos</h2>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Buscar pedidos…"
            className="w-full rounded border border-[#d59106]/50 bg-[#1a1a1a] px-3 py-2 text-orange-100 placeholder:text-orange-100/40"
            aria-label="Buscar pedidos"
          />
          <div className="flex-1 min-h-[12rem] max-h-[24rem] overflow-y-auto space-y-2">
            {loadingProducts ? (
              <p className="text-sm text-orange-100/60">Carregando…</p>
            ) : products.length === 0 ? (
              <p className="text-sm text-orange-100/60">Nenhum pedido encontrado.</p>
            ) : (
              products.map((product) => {
                const checked = selectedProductIds.has(product._id);
                return (
                  <label
                    key={product._id}
                    className="flex items-center gap-2 rounded border border-[#d59106]/35 bg-[#141414] px-2 py-2 cursor-pointer hover:bg-[#1f1f1f]"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProduct(product._id)}
                      className="accent-[#d59106]"
                    />
                    <span className="text-sm">{productLabel(product)}</span>
                  </label>
                );
              })
            )}
          </div>
        </section>

        <section className="border border-[#d59106] rounded p-4 flex flex-col gap-3">
          <h2 className="text-[#d59106] font-semibold">Seriais</h2>
          <p>Um serial por linha</p>
          <textarea
            value={serialText}
            onChange={(event) => setSerialText(event.target.value)}
            placeholder={SERIAL_PLACEHOLDER}
            rows={8}
            className="w-full rounded border border-[#d59106]/50 bg-[#1a1a1a] px-3 py-2 text-orange-100 placeholder:text-orange-100/40 font-mono text-sm"
            aria-label="Lista de seriais"
          />
          <p className="text-sm text-orange-100/70">
            {serialCount} reconhecido(s)
            {duplicateCount > 0 ? ` · ${duplicateCount} duplicado(s)` : ""}
          </p>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#d59106]/50 bg-[#1a1a1a]/95 backdrop-blur-sm px-4 py-3 flex items-center justify-between gap-4 shadow-[0_-4px_16px_rgba(0,0,0,0.35)]">
        <p className="text-sm text-orange-100/80">
          {productCount} pedidos · {serialCount} seriais
        </p>
        <Button
          click={handleContinue}
          disabled={!canContinue}
          classes="bg-[#d59106] hover:bg-[#e6a307] disabled:opacity-50 rounded px-4 py-2 font-semibold"
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
